const COHORT_HISTORY_KEY = 'cohort-history';

export interface CohortHistoryEntry {
  title: string;
  cohortId: `0x${string}`;
  lastAccessed: number;
  participantAddresses?: Record<string, string>; // name -> address (for distribution cache)
}

/**
 * Get list of recently accessed cohorts (for dropdown)
 */
export function getCohortHistory(): CohortHistoryEntry[] {
  const stored = localStorage.getItem(COHORT_HISTORY_KEY);
  if (!stored) return [];
  return JSON.parse(stored).sort(
    (a: CohortHistoryEntry, b: CohortHistoryEntry) => b.lastAccessed - a.lastAccessed
  );
}

/**
 * Add or update a cohort in history
 */
export function addToCohortHistory(title: string, cohortId: `0x${string}`): void {
  const history = getCohortHistory();
  const existing = history.findIndex(h => h.cohortId === cohortId);

  if (existing >= 0) {
    history[existing].lastAccessed = Date.now();
    history[existing].title = title; // Update title in case it changed
  } else {
    history.push({ title, cohortId, lastAccessed: Date.now() });
  }

  // Keep last 20 entries
  const trimmed = history
    .sort((a, b) => b.lastAccessed - a.lastAccessed)
    .slice(0, 20);

  localStorage.setItem(COHORT_HISTORY_KEY, JSON.stringify(trimmed));
}

/**
 * Update participant addresses for a cohort (for distribution cache)
 */
export function updateParticipantAddresses(cohortId: `0x${string}`, addresses: Record<string, string>): void {
  const history = getCohortHistory();
  const existing = history.findIndex(h => h.cohortId === cohortId);

  if (existing >= 0) {
    history[existing].participantAddresses = addresses;
    localStorage.setItem(COHORT_HISTORY_KEY, JSON.stringify(history));
  }
}

/**
 * Remove a cohort from history
 */
export function removeFromCohortHistory(cohortId: `0x${string}`): void {
  const history = getCohortHistory().filter(h => h.cohortId !== cohortId);
  localStorage.setItem(COHORT_HISTORY_KEY, JSON.stringify(history));
}

/**
 * Get a cohort entry by ID (for looking up title)
 */
export function getCohortById(cohortId: `0x${string}`): CohortHistoryEntry | undefined {
  return getCohortHistory().find(h => h.cohortId === cohortId);
}
